// helpers/diag.ts
import { L } from '../../../../utils/logger';
import { safeConnectCtx, ctxOk } from '../../bufferTransport/ctxUtil';
import { getMasterChainVersion } from '../../graphNodes';
// Throttle especially chatty chain snapshots
const sayChain = L.throttled('media:chain', 'snapshot', 1000);

export function diagOn() {
  try {
    const w = window as any;
    if (w.DIAG_MEDIA === true) return true;
    const v = localStorage.getItem('DIAG_MEDIA');
    return v === '1' || v === 'true';
  } catch {}
  return false;
}

export function tagNode(n?: AudioNode | null) {
  if (!n) return 'none';
  const any = n as any;
  if (!any.__gid) any.__gid = Math.random().toString(36).slice(2, 8);
  return any.__gid;
}

export function logMasterChain(refs: any, where: string) {
  if (!diagOn()) return;
  const ctx: AudioContext | null = refs.audioCtxRef?.current ?? null;
  const master = refs.masterGainRef?.current ?? null;
  const pre = refs.masterAnalyserPreRef?.current ?? null;
  const gate = refs.gateGainRef?.current ?? null;
  const hp = refs.masterHighpassRef?.current ?? null;
  const lp = refs.masterFilterRef?.current ?? null;
  const ana = refs.masterAnalyserRef?.current ?? null;

  sayChain('info', where, {
    ctx: ctx?.state,
    nodes: {
      master: tagNode(master),
      pre: tagNode(pre),
      gate: tagNode(gate),
      hp: tagNode(hp),
      lp: tagNode(lp),
      ana: tagNode(ana),
    },
    gains: {
      master: (master as any)?.gain?.value ?? null,
      gate: (gate as any)?.gain?.value ?? null,
    },
    fft: { pre: pre?.fftSize ?? null, ana: ana?.fftSize ?? null },
  });
}

export function logScheduled(refs: any, where: string) {
  if (!diagOn()) return;
  const sched = refs?.scheduledRef?.current || null;
  const roles = sched ? Object.keys(sched) : [];
  const out: any = {};
  for (const r of roles) {
    const s = sched[r];
    out[r] = {
      gain: s?.gain?.gain?.value ?? null,
      pr: (s?.source as any)?.playbackRate?.value ?? null,
      // For RB worklet if present:
      rb: !!s?.worklet,
    };
  }
  L.info('media:scheduled', where, { roles, stems: out });
}

export function logUnexpectedSilence(refs: any, reason: string, extra?: Record<string, any>) {
  const ctx: AudioContext | null = refs.audioCtxRef?.current ?? null;
  const gate = refs.gateGainRef?.current ?? null;
  const master = refs.masterGainRef?.current ?? null;
  const gateGid = tagNode(gate);
  const masterGid = tagNode(master);
  const gateVal = (gate as any)?.gain?.value ?? null;
  const masterVal = (master as any)?.gain?.value ?? null;
  const sched = refs?.scheduledRef?.current || null;
  const roles = sched ? Object.keys(sched) : [];
  L.warn('media:silence', reason, {
    ctx: ctx?.state, gateGid, masterGid, gateVal, masterVal, roles, ...(extra || {}),
  });
}

// Create persistent meters once and reuse them. Call setupHeartbeatMeters(refs, ctx)
// after master chain exists. Then call startHeartbeat(refs, ctx) to log compact snapshots.
export function setupHeartbeatMeters(refs: any, ctx: AudioContext) {
  try {
    if ((refs as any)._hbMeters) return (refs as any)._hbMeters;
    const an = () => ctx.createAnalyser();
    const meters = {
      mix: an(),
      master: an(),
      gate: an(),
      hpf: an(),
      lpf: an(),
      bypass: an(),
      dest: an(),
    } as const;
    // connect once to passive points (mix via tee if present)
    const tee: GainNode | null = (refs as any).teeGainRef?.current ?? null;
    const mixBus: GainNode | null = tee ?? ((refs as any).masterGainRef?.current ?? null);
    const masterGain: GainNode | null = (refs as any).masterGainRef?.current ?? null;
    const gateGain: GainNode | null = (refs as any).gateGainRef?.current ?? null;
    const masterHighpass: BiquadFilterNode | null = (refs as any).masterHighpassRef?.current ?? null;
    const masterFilter: BiquadFilterNode | null = (refs as any).masterFilterRef?.current ?? null;
    const bypassGain: GainNode | null = (refs as any).bypassGainRef?.current ?? null;
    const liveCtx: AudioContext | null = (refs as any).audioCtxRef?.current ?? null;
    if (liveCtx && ctxOk(liveCtx)) {
      try { mixBus && safeConnectCtx(liveCtx, mixBus, meters.mix); } catch {}
      try { masterGain && safeConnectCtx(liveCtx, masterGain, meters.master); } catch {}
      try { gateGain && safeConnectCtx(liveCtx, gateGain, meters.gate); } catch {}
      try { masterHighpass && safeConnectCtx(liveCtx, masterHighpass, meters.hpf); } catch {}
      try { masterFilter && safeConnectCtx(liveCtx, masterFilter, meters.lpf); } catch {}
      try { bypassGain && safeConnectCtx(liveCtx, bypassGain, meters.bypass); } catch {}
      try { bypassGain && safeConnectCtx(liveCtx, bypassGain, meters.dest); } catch {}
    }
    (refs as any)._hbMeters = meters;
    return meters;
  } catch {}
  return (refs as any)._hbMeters;
}

function rmsDb(an: AnalyserNode): number {
  try {
    const buf = new Float32Array(an.fftSize || 2048);
    an.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length) || 1e-12;
    return 20 * Math.log10(rms);
  } catch { return -120; }
}

export function startHeartbeat(refs: any, ctx: AudioContext, intervalMs = 1000) {
  try { setupHeartbeatMeters(refs, ctx); } catch {}
  if ((refs as any)._hbTimer) return;
  const timer = setInterval(() => {
    try {
      const m = (refs as any)._hbMeters || {};
      const gate: GainNode | null = (refs as any).gateGainRef?.current ?? null;
      const snap = {
        t: Number(ctx.currentTime.toFixed(3)),
        db: {
          mix: m.mix ? rmsDb(m.mix) : null,
          master: m.master ? rmsDb(m.master) : null,
          gate_out: m.gate ? rmsDb(m.gate) : null,
          hpf: m.hpf ? rmsDb(m.hpf) : null,
          lpf: m.lpf ? rmsDb(m.lpf) : null,
          bypass: m.bypass ? rmsDb(m.bypass) : null,
          dest: m.dest ? rmsDb(m.dest) : null,
        },
        gate: (gate as any)?.gain?.value ?? null,
        chainVersion: getMasterChainVersion(),
      };
      L.info('media:diag', 'heartbeat', snap);
    } catch {}
  }, intervalMs);
  (refs as any)._hbTimer = timer;
}
